/**
 * Browser client for the 카톡 성적표 e-mail intake (docs/kakao-report-card-intake.md).
 *
 * Same-origin API only. The secret travels in `Authorization: Bearer`, never in
 * a URL; requests carry no cookies, no referrer and bypass the HTTP cache.
 * Every response is validated before use, so an HTML fallback page, a proxy
 * error or a malformed value is never mistaken for a session or a file.
 *
 * Clocks: a phone clock can be minutes or hours off, so the server's absolute
 * `expiresAt` is only shape-checked and never compared with this device's
 * clock. Every answer also carries `expiresInMs`, measured by the server when
 * it answered; the page's deadline is the moment the answer arrived plus that,
 * on this device's clock (IntakeSession.deadline, IntakeStatusUpdate.deadline).
 */
import {
  INTAKE_API_BASE,
  INTAKE_ID_ALPHABET,
  type IntakeFailure,
  type IntakeFileInfo,
  type IntakeState,
  type IntakeStatus,
} from './types';

/** Resume entries live under `${INTAKE_STORAGE_KEY}:{id}`, one per address (§7.1-5). */
export const INTAKE_STORAGE_KEY = 'oneultool:krc-intake';
export const INTAKE_SESSION_TTL_MS = 60 * 60_000;
/** How often the page should poll while waiting (§7.1). */
export const INTAKE_POLL_INTERVAL_MS = 3_000;
/**
 * How long past its local deadline a waiting page keeps an address it cannot
 * confirm with the server (offline, service down). It only absorbs small clock
 * corrections on this device: the server never keeps a session past its own expiry.
 */
export const INTAKE_EXPIRY_GRACE_MS = 2 * 60_000;
export const INTAKE_MAX_FILES = 10;
/** Server-side cap for one e-mail; kept files are never larger. */
export const INTAKE_MAX_MESSAGE_BYTES = 40 * 1024 * 1024;
export const INTAKE_EXPIRED_MESSAGE = '주소가 만료됐어요. 새 주소를 받아 주세요.';
/** 429: per-client limit. Shared IPs (mobile carriers) hit it too, so it blames no one. */
export const INTAKE_RATE_LIMITED_MESSAGE = '요청이 많아요. 잠시 후 다시 시도해 주세요.';
/** 503 INTAKE_BUSY: the service cannot hold another address right now. */
export const INTAKE_BUSY_MESSAGE = '지금은 메일 받기 이용이 많아요. 잠시 후 다시 시도하거나 ‘파일로 올리기’를 이용해 주세요.';

const REQUEST_TIMEOUT_MS = 15_000;
/** Downloads may be slow on mobile: the timer restarts with every chunk. */
const DOWNLOAD_IDLE_TIMEOUT_MS = 30_000;
/** expiresInMs is measured by the server, so there is no clock skew to allow for: only slack. */
const EXPIRES_IN_SLACK_MS = 60_000;
/** A fresh address must have at least this long left. */
const MIN_REMAINING_MS = 30_000;
/** Shape check for epoch milliseconds: rejects seconds and small numbers. */
const MIN_EPOCH_MS = 1e12;
/** A stored deadline further ahead than this is garbage, whatever this device's clock did. */
const MAX_STORED_AHEAD_MS = 24 * 60 * 60_000;
const STORAGE_PREFIX = `${INTAKE_STORAGE_KEY}:`;
const ID_PATTERN = new RegExp(`^[${INTAKE_ID_ALPHABET}]{10}$`);
const SECRET_PATTERN = /^[0-9a-f]{32}$/;
const DOMAIN_PATTERN = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;
const FILE_NAME_PATTERN = /^[^\p{Cc}/\\]{1,250}\.(?:txt|zip|csv)$/iu;
/** Unpaired UTF-16 surrogates (only in broken or hostile names). */
const LONE_SURROGATES = /\p{Cs}/gu;
const STATES = new Set<string>(['waiting', 'received', 'expired'] satisfies IntakeState[]);
const FAILURES = new Set<string>(['NO_ATTACHMENT', 'LINK_ONLY', 'TOO_LARGE', 'UNSUPPORTED_ATTACHMENT', 'MAIL_UNREADABLE'] satisfies IntakeFailure[]);
/** What a failure code looks like, including ones a newer service may add. */
const FAILURE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
/** Shown for a failure code this page does not know yet (the service was updated first). */
const UNKNOWN_FAILURE: IntakeFailure = 'MAIL_UNREADABLE';

const FAILURE_MESSAGES: Record<IntakeFailure, string> = {
  NO_ATTACHMENT: '메일에 대화 파일이 없었어요. 카톡에서 대화를 다시 내보내고, 그때 열리는 메일로 보내 주세요(새로 쓴 메일에는 파일이 없어요).',
  LINK_ONLY: '파일이 커서 첨부 대신 링크로 왔어요. 링크는 열 수 없어요. 파일로 저장해서 ‘파일로 올리기’를 이용해 주세요.',
  TOO_LARGE: '메일이 40MB보다 커요. ‘텍스트 메시지만’으로 다시 내보내 주세요.',
  UNSUPPORTED_ATTACHMENT: '카톡 대화 파일(.txt/.zip/.csv)이 아니었어요.',
  MAIL_UNREADABLE: '메일을 읽지 못했어요. 카톡에서 대화를 다시 내보내고, 그때 열리는 메일에 다른 파일을 더하지 말고 그대로 보내 주세요.',
};

export type IntakeClientErrorCode =
  | 'NETWORK' // offline, DNS, connection reset
  | 'TIMEOUT'
  | 'ABORTED' // the caller's AbortSignal fired
  | 'RATE_LIMITED' // 429, see retryAfterSeconds
  | 'BUSY' // 503 {"error":"INTAKE_BUSY"}: no room for another address, see retryAfterSeconds
  | 'UNAVAILABLE' // other 5xx, e.g. the intake service is down behind nginx
  | 'NOT_FOUND' // the session (or a file) is gone: expired, deleted or server restarted
  | 'REJECTED' // 4xx such as 403 (page origin not allowed)
  | 'INVALID_RESPONSE'; // not the response this API sends

const ERROR_MESSAGES: Record<IntakeClientErrorCode, string> = {
  NETWORK: '인터넷 연결을 확인하고 다시 시도해 주세요.',
  TIMEOUT: '응답이 늦어요. 잠시 후 다시 시도해 주세요.',
  ABORTED: '요청을 취소했어요.',
  RATE_LIMITED: INTAKE_RATE_LIMITED_MESSAGE,
  BUSY: INTAKE_BUSY_MESSAGE,
  UNAVAILABLE: '메일 받기에 잠시 문제가 있어요. 잠시 후 다시 시도하거나 ‘파일로 올리기’를 이용해 주세요.',
  NOT_FOUND: INTAKE_EXPIRED_MESSAGE,
  REJECTED: '이 페이지에서는 메일 받기를 시작할 수 없어요. 오늘툴에서 다시 열어 주세요.',
  INVALID_RESPONSE: '서버 응답을 읽지 못했어요. 잠시 후 다시 시도해 주세요.',
};

/**
 * All client failures; `message` is Korean copy the page can show as is.
 * Aborts use the conventional name 'AbortError' (code 'ABORTED').
 */
export class IntakeClientError extends Error {
  readonly code: IntakeClientErrorCode;
  /** HTTP status when a response arrived. */
  readonly status: number | null;
  /** Seconds from Retry-After on 429 and 503 INTAKE_BUSY, when the server sent one. */
  readonly retryAfterSeconds: number | null;

  constructor(code: IntakeClientErrorCode, details: { readonly status?: number; readonly retryAfterSeconds?: number | null } = {}) {
    super(ERROR_MESSAGES[code]);
    this.name = code === 'ABORTED' ? 'AbortError' : 'IntakeClientError';
    this.code = code;
    this.status = details.status ?? null;
    this.retryAfterSeconds = details.retryAfterSeconds ?? null;
  }
}

/**
 * An address as this page keeps it (the server's IntakeSessionCreated without
 * its clock values). `deadline` is on this device's clock: when the server's
 * answer arrived plus its expiresInMs.
 */
export interface IntakeSession {
  /** 10 chars from INTAKE_ID_ALPHABET; also the local part of the address. */
  readonly id: string;
  /** 32 lowercase hex chars. Sent only as "Authorization: Bearer <secret>". */
  readonly secret: string;
  readonly address: string;
  /** Epoch ms on this device's clock after which the address is gone. */
  readonly deadline: number;
}

/** A validated IntakeStatus, with its expiry turned into a deadline on this device's clock. */
export interface IntakeStatusUpdate {
  readonly state: IntakeState;
  /** Epoch ms on this device's clock: when the answer arrived plus expiresInMs. */
  readonly deadline: number;
  /** Present when state === 'received'. */
  readonly files?: readonly IntakeFileInfo[];
  /** Last rejected e-mail while still waiting (the page keeps waiting). */
  readonly lastFailure?: IntakeFailure;
}

/** What requests need from a session. Any IntakeSession fits. */
export type IntakeSessionRef = Pick<IntakeSession, 'id' | 'secret'>;

/** The 'received' answer a download starts from: the page's view of it or the server's. */
export type IntakeReceivedRef = IntakeStatusUpdate | IntakeStatus;

export interface IntakeRequestOptions {
  readonly signal?: AbortSignal;
}

export interface IntakeDownloadOptions extends IntakeRequestOptions {
  /** Bytes received so far across all files, and the expected total. */
  readonly onProgress?: (loadedBytes: number, totalBytes: number) => void;
}

export interface IntakeDeleteOptions extends IntakeRequestOptions {
  /** Let the request outlive the page (e.g. when deleting on pagehide). */
  readonly keepalive?: boolean;
}

/** Every request function takes its options object or just an AbortSignal. */
export type IntakeOptionsArgument<T extends IntakeRequestOptions> = T | AbortSignal | undefined;

/** Copy for IntakeStatus.lastFailure (§7.1). The same address keeps working. */
export function intakeFailureMessage(failure: IntakeFailure): string {
  return FAILURE_MESSAGES[failure];
}

/** True once this device's clock has reached the session's deadline. */
export function isIntakeSessionExpired(session: Pick<IntakeSession, 'deadline'>, now = Date.now()): boolean {
  return session.deadline <= now;
}

/** POST /sessions: a fresh address. Throws IntakeClientError. */
export async function createIntakeSession(argument?: IntakeOptionsArgument<IntakeRequestOptions>): Promise<IntakeSession> {
  const options = requestOptions(argument);
  const timer = startTimer(REQUEST_TIMEOUT_MS, options.signal);
  try {
    const response = await send(`${INTAKE_API_BASE}sessions`, { method: 'POST' }, timer);
    if (response.status !== 201) throw await statusError(response, timer);
    const body = await readJson(response, timer);
    const session = parseCreated(body, Date.now());
    if (!session) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
    return session;
  } finally {
    timer.dispose();
  }
}

/**
 * GET /sessions/{id}. A session the server no longer knows (404: expired,
 * deleted, or the service restarted) resolves as `{ state: 'expired' }`.
 */
export async function fetchIntakeStatus(
  session: IntakeSessionRef,
  argument?: IntakeOptionsArgument<IntakeRequestOptions>,
): Promise<IntakeStatusUpdate> {
  if (!isSessionRef(session)) return expiredStatus();
  const options = requestOptions(argument);
  const timer = startTimer(REQUEST_TIMEOUT_MS, options.signal);
  try {
    const response = await send(sessionPath(session.id), { headers: authorization(session) }, timer);
    if (response.status === 404) return expiredStatus();
    if (response.status !== 200) throw await statusError(response, timer);
    const body = await readJson(response, timer);
    const status = parseStatus(body, Date.now());
    if (!status) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
    return status;
  } finally {
    timer.dispose();
  }
}

/**
 * Downloads every received file as File objects with the original names, in
 * order. Pass the 'received' status (or its `files`); sizes are checked
 * against it.
 */
export async function downloadIntakeFiles(
  session: IntakeSessionRef,
  received: IntakeReceivedRef | readonly IntakeFileInfo[],
  argument?: IntakeOptionsArgument<IntakeDownloadOptions>,
): Promise<File[]> {
  if (!isSessionRef(session)) throw new IntakeClientError('NOT_FOUND');
  const options: IntakeDownloadOptions = requestOptions(argument);
  const listed = isFileList(received) ? received : received.files;
  if (!isFileList(received) && received.state === 'expired') throw new IntakeClientError('NOT_FOUND');
  const expected = parseFiles(listed);
  if (!expected) throw new IntakeClientError('INVALID_RESPONSE');
  const totalBytes = expected.reduce((total, file) => total + file.size, 0);
  let loadedBytes = 0;
  const downloaded: File[] = [];
  for (const [index, info] of expected.entries()) {
    const timer = startTimer(DOWNLOAD_IDLE_TIMEOUT_MS, options.signal);
    try {
      const response = await send(`${sessionPath(session.id)}/files/${index}`, { headers: authorization(session) }, timer);
      if (response.status !== 200) throw await statusError(response, timer);
      if (!/^application\/octet-stream\b/i.test(response.headers.get('content-type') ?? '')) {
        throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
      }
      const declared = response.headers.get('content-length');
      if (declared !== null && Number(declared) !== info.size) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
      const parts = await readBody(response, info.size, timer, (bytes) => {
        loadedBytes += bytes;
        options.onProgress?.(loadedBytes, totalBytes);
      });
      downloaded.push(new File(parts, info.name, { type: fileType(info.name), lastModified: Date.now() }));
    } finally {
      timer.dispose();
    }
  }
  return downloaded;
}

/** DELETE /sessions/{id}. Already-gone sessions count as deleted. */
export async function deleteIntakeSession(session: IntakeSessionRef, argument?: IntakeOptionsArgument<IntakeDeleteOptions>): Promise<void> {
  if (!isSessionRef(session)) return;
  const options: IntakeDeleteOptions = requestOptions(argument);
  const timer = startTimer(REQUEST_TIMEOUT_MS, options.signal);
  try {
    const response = await send(sessionPath(session.id), {
      method: 'DELETE',
      headers: authorization(session),
      keepalive: options.keepalive === true,
    }, timer);
    if (response.status !== 204 && response.status !== 404) throw await statusError(response, timer);
  } finally {
    timer.dispose();
  }
}

// ---------------------------------------------------------------------------
// Resume storage (§7.1 #5): the page reopens and keeps waiting until expiry.
// Each address has its own entry, so one tab can never overwrite or delete
// another tab's address. An address the person deleted keeps its entry, marked
// `deleting`, until the server confirms the DELETE (§7.1 #3): it is never
// resumed, and the secret stays only so the DELETE can be sent again.
// None of these functions throws, whatever the storage does.
// ---------------------------------------------------------------------------

/** One resume entry: the address, and whether it only waits for its DELETE to get through. */
interface StoredEntry {
  readonly session: IntakeSession;
  readonly deleting: boolean;
}

/** The storage key of one address's resume entry. */
export function intakeStorageKey(id: string): string {
  return `${STORAGE_PREFIX}${id}`;
}

/** Remembers the session for the "이어하기" flow. Returns false when storage is unavailable. */
export function saveIntakeSession(session: IntakeSession, storage: Storage | null = defaultStorage()): boolean {
  return writeEntry(session, false, storage);
}

/**
 * Marks the address as being deleted: it is never resumed again, and its entry
 * keeps the secret only until the server confirms the DELETE or the address
 * expires on this device. Returns false when storage is unavailable.
 */
export function markIntakeSessionDeleting(session: IntakeSession, storage: Storage | null = defaultStorage()): boolean {
  return writeEntry(session, true, storage);
}

/**
 * Every remembered address that has not expired on this device and is not
 * being deleted, newest first. Expired and malformed entries (and the old
 * single-key entry) are removed; entries being deleted are kept.
 */
export function loadIntakeSessions(storage: Storage | null = defaultStorage(), now = Date.now()): IntakeSession[] {
  return readEntries(storage, now)
    .filter((entry) => !entry.deleting)
    .map((entry) => entry.session)
    .sort((a, b) => b.deadline - a.deadline);
}

/** The newest remembered address, or null. Expired or malformed entries are removed. */
export function loadIntakeSession(storage: Storage | null = defaultStorage(), now = Date.now()): IntakeSession | null {
  return loadIntakeSessions(storage, now)[0] ?? null;
}

/**
 * Addresses whose DELETE the server has not confirmed yet. Once one expires on
 * this device the server has dropped it anyway, so its entry is removed.
 */
export function loadDeletingIntakeSessions(storage: Storage | null = defaultStorage(), now = Date.now()): IntakeSession[] {
  return readEntries(storage, now)
    .filter((entry) => entry.deleting)
    .map((entry) => entry.session);
}

/** Forgets one address. Other addresses (other tabs' sessions) are left alone. */
export function clearIntakeSession(session: Pick<IntakeSession, 'id'>, storage: Storage | null = defaultStorage()): void {
  if (storage && ID_PATTERN.test(session.id)) removeStorageKey(storage, intakeStorageKey(session.id));
}

// ---------------------------------------------------------------------------
// This tab's address (§7.1 #6). sessionStorage survives reloads and restores
// of the same tab but is not shared with other tabs, so a reloaded tab goes
// back to the address it used instead of the newest one, which another tab may
// be using. Only the id is kept here, never the secret.
// ---------------------------------------------------------------------------

/** sessionStorage key holding the id of the address this tab uses. */
export const INTAKE_TAB_STORAGE_KEY = 'oneultool:krc-intake-tab';

/** Remembers that this tab uses the address. Returns false when storage is unavailable. */
export function rememberIntakeTabSession(id: string, storage: Storage | null = defaultTabStorage()): boolean {
  if (!storage || !ID_PATTERN.test(id)) return false;
  try {
    if (storage.getItem(INTAKE_TAB_STORAGE_KEY) !== id) storage.setItem(INTAKE_TAB_STORAGE_KEY, id);
    return true;
  } catch {
    return false;
  }
}

/** The id of the address this tab used, e.g. before it reloaded, or null. */
export function loadIntakeTabSession(storage: Storage | null = defaultTabStorage()): string | null {
  if (!storage) return null;
  try {
    const id = storage.getItem(INTAKE_TAB_STORAGE_KEY);
    return id !== null && ID_PATTERN.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** Forgets this tab's address, but only when it is `id` (the tab may use another one by now). */
export function forgetIntakeTabSession(id: string, storage: Storage | null = defaultTabStorage()): void {
  if (!storage) return;
  try {
    if (storage.getItem(INTAKE_TAB_STORAGE_KEY) === id) storage.removeItem(INTAKE_TAB_STORAGE_KEY);
  } catch {
    // Blocked storage: nothing was remembered.
  }
}

// ---------------------------------------------------------------------------
// One page per address (Web Locks)
// ---------------------------------------------------------------------------

/**
 * Which open page uses which address. A page that polls or downloads an
 * address holds a Web Lock named after it, and taking an address steals that
 * lock: the page the person is looking at always wins, and the page that lost
 * it hears about it (onLost) and stops without clearing or deleting anything.
 * So two tabs never poll, download and delete the same address. Without Web
 * Locks (older browsers) every take succeeds and nothing is ever lost.
 */
export interface IntakeClaims {
  /** Holds the address in this page, taking it over from any other page. */
  take(id: string): Promise<void>;
  /** Stops holding the address, e.g. once it is finished, cancelled or expired. */
  release(id: string): void;
  /** True after another page took the address over, until this page takes it again. */
  isLost(id: string): boolean;
  /** Called with the id when another page takes over an address this page holds. Returns an unsubscribe function. */
  onLost(listener: (id: string) => void): () => void;
}

/** The part of the Web Locks API the claims use. */
export type IntakeLockManager = Pick<LockManager, 'request'>;

interface ClaimHold {
  granted: boolean;
  released: boolean;
  free: () => void;
  readonly ready: Promise<void>;
}

export function createIntakeClaims(locks: IntakeLockManager | null = defaultLocks()): IntakeClaims {
  const held = new Map<string, ClaimHold>();
  const lost = new Set<string>();
  const listeners = new Set<(id: string) => void>();

  function take(id: string): Promise<void> {
    lost.delete(id);
    const current = held.get(id);
    if (current) return current.ready;
    if (!locks) return Promise.resolve();
    let markReady: () => void = () => undefined;
    const hold: ClaimHold = {
      granted: false,
      released: false,
      free: () => undefined,
      ready: new Promise<void>((resolve) => {
        markReady = resolve;
      }),
    };
    held.set(id, hold);
    let request: Promise<unknown>;
    try {
      request = locks.request(intakeStorageKey(id), { steal: true }, () => {
        if (hold.released) return undefined;
        hold.granted = true;
        markReady();
        return new Promise<void>((resolve) => {
          hold.free = resolve;
        });
      });
    } catch {
      // e.g. a sandboxed frame without lock access: behave as without Web Locks.
      held.delete(id);
      markReady();
      return hold.ready;
    }
    request.then(
      () => {
        if (held.get(id) === hold) held.delete(id);
        markReady();
      },
      (error: unknown) => {
        markReady();
        if (held.get(id) !== hold) return;
        held.delete(id);
        // A stolen lock rejects with AbortError: another page took the address.
        if (!hold.granted || !isAbortError(error)) return;
        lost.add(id);
        for (const listener of [...listeners]) listener(id);
      },
    );
    return hold.ready;
  }

  return {
    take,
    release(id) {
      const hold = held.get(id);
      if (!hold) return;
      held.delete(id);
      hold.released = true;
      hold.free();
    },
    isLost: (id) => lost.has(id),
    onLost(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function requestOptions<T extends IntakeRequestOptions>(argument: IntakeOptionsArgument<T>): T | IntakeRequestOptions {
  if (argument === undefined) return {};
  if (isAbortSignal(argument)) return { signal: argument };
  return argument;
}

function isAbortSignal(value: unknown): value is AbortSignal {
  return typeof AbortSignal !== 'undefined' && value instanceof AbortSignal;
}

function isFileList(value: IntakeReceivedRef | readonly IntakeFileInfo[]): value is readonly IntakeFileInfo[] {
  return Array.isArray(value);
}

interface RequestTimer {
  readonly signal: AbortSignal;
  readonly timedOut: boolean;
  /** Restarts the countdown (idle timeout for downloads). */
  restart(): void;
  dispose(): void;
}

function startTimer(timeoutMs: number, outer?: AbortSignal): RequestTimer {
  const controller = new AbortController();
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onAbort = () => controller.abort();
  const restart = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
  };
  if (outer?.aborted) controller.abort();
  else outer?.addEventListener('abort', onAbort, { once: true });
  restart();
  return {
    signal: controller.signal,
    get timedOut() {
      return timedOut;
    },
    restart,
    dispose() {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onAbort);
    },
  };
}

async function send(path: string, init: RequestInit, timer: RequestTimer): Promise<Response> {
  if (timer.signal.aborted) throw transportError(timer);
  try {
    return await fetch(path, {
      ...init,
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: timer.signal,
    });
  } catch {
    throw transportError(timer);
  }
}

function transportError(timer: RequestTimer): IntakeClientError {
  if (timer.timedOut) return new IntakeClientError('TIMEOUT');
  if (timer.signal.aborted) return new IntakeClientError('ABORTED');
  return new IntakeClientError('NETWORK');
}

async function statusError(response: Response, timer: RequestTimer): Promise<IntakeClientError> {
  const { status } = response;
  if (status === 429) return new IntakeClientError('RATE_LIMITED', { status, retryAfterSeconds: retryAfterSeconds(response) });
  if (status === 503 && (await errorBodyCode(response)) === 'INTAKE_BUSY') {
    return new IntakeClientError('BUSY', { status, retryAfterSeconds: retryAfterSeconds(response) });
  }
  if (timer.signal.aborted) return transportError(timer);
  if (status === 404) return new IntakeClientError('NOT_FOUND', { status });
  if (status >= 500) return new IntakeClientError('UNAVAILABLE', { status });
  if (status >= 400) return new IntakeClientError('REJECTED', { status });
  return new IntakeClientError('INVALID_RESPONSE', { status });
}

/** The `error` code of a JSON error body (`{"error":"INTAKE_BUSY"}`), or null for anything else. */
async function errorBodyCode(response: Response): Promise<string | null> {
  if (!/^application\/json\b/i.test(response.headers.get('content-type') ?? '')) return null;
  try {
    const body: unknown = await response.json();
    return isRecord(body) && typeof body.error === 'string' ? body.error : null;
  } catch {
    return null;
  }
}

function retryAfterSeconds(response: Response): number | null {
  const value = response.headers.get('retry-after');
  return value !== null && /^\d{1,6}$/.test(value.trim()) ? Number(value.trim()) : null;
}

async function readJson(response: Response, timer: RequestTimer): Promise<unknown> {
  if (!/^application\/json\b/i.test(response.headers.get('content-type') ?? '')) {
    throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
  }
  try {
    return await response.json();
  } catch {
    if (timer.signal.aborted) throw transportError(timer);
    throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
  }
}

/** Reads exactly expectedBytes, restarting the idle timer on every chunk. */
async function readBody(
  response: Response,
  expectedBytes: number,
  timer: RequestTimer,
  onChunk: (bytes: number) => void,
): Promise<Uint8Array<ArrayBuffer>[]> {
  const reader = response.body?.getReader();
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let received = 0;
  if (!reader) {
    const whole = new Uint8Array(await response.arrayBuffer().catch(() => {
      throw transportError(timer);
    }));
    if (whole.byteLength !== expectedBytes) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
    onChunk(whole.byteLength);
    return [whole];
  }
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      received += value.byteLength;
      if (received > expectedBytes) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
      parts.push(value);
      timer.restart();
      onChunk(value.byteLength);
    }
  } catch (error) {
    void reader.cancel().catch(() => undefined);
    throw error instanceof IntakeClientError ? error : transportError(timer);
  }
  if (received !== expectedBytes) throw new IntakeClientError('INVALID_RESPONSE', { status: response.status });
  return parts;
}

/** id, secret and address of a created or stored session, or null. */
function parseAddress(value: Record<string, unknown>): Pick<IntakeSession, 'id' | 'secret' | 'address'> | null {
  const { id, secret, address } = value;
  if (typeof id !== 'string' || !ID_PATTERN.test(id)) return null;
  if (typeof secret !== 'string' || !SECRET_PATTERN.test(secret)) return null;
  if (typeof address !== 'string' || !address.startsWith(`${id}@`) || !DOMAIN_PATTERN.test(address.slice(id.length + 1))) return null;
  return { id, secret, address };
}

/** A 201 answer to POST /sessions, received at `receivedAt` on this device's clock. */
function parseCreated(value: unknown, receivedAt: number): IntakeSession | null {
  if (!isRecord(value)) return null;
  const parts = parseAddress(value);
  const { expiresAt, expiresInMs } = value;
  // expiresAt is on the server's clock: shape only, never compared with ours.
  if (!parts || !isEpochMs(expiresAt) || !isExpiresIn(expiresInMs) || expiresInMs < MIN_REMAINING_MS) return null;
  return { ...parts, deadline: deadlineFrom(receivedAt, expiresInMs) };
}

/** A resume entry. It is kept only while it has not expired on this device. */
function parseStored(value: unknown, now: number): StoredEntry | null {
  if (!isRecord(value)) return null;
  const parts = parseAddress(value);
  const { deadline, deleting } = value;
  if (!parts || !isEpochMs(deadline) || deadline <= now || deadline > now + MAX_STORED_AHEAD_MS) return null;
  if (deleting !== undefined && deleting !== true) return null;
  return { session: { ...parts, deadline }, deleting: deleting === true };
}

function parseStatus(value: unknown, receivedAt: number): IntakeStatusUpdate | null {
  if (!isRecord(value)) return null;
  const { state, expiresAt, expiresInMs, files, lastFailure } = value;
  if (typeof state !== 'string' || !STATES.has(state)) return null;
  if (!isEpochMs(expiresAt) || !isExpiresIn(expiresInMs)) return null;
  if (lastFailure !== undefined && (state !== 'waiting' || typeof lastFailure !== 'string' || !FAILURE_CODE_PATTERN.test(lastFailure))) return null;
  const deadline = deadlineFrom(receivedAt, expiresInMs);
  if (state === 'received') {
    const parsed = parseFiles(files);
    return parsed ? { state, deadline, files: parsed } : null;
  }
  if (files !== undefined) return null;
  const typedState = state as Exclude<IntakeState, 'received'>;
  if (lastFailure === undefined) return { state: typedState, deadline };
  // A code from a newer service still says the e-mail was not used: the page
  // keeps waiting and explains it generically instead of failing every poll.
  const code = lastFailure as string;
  return { state: typedState, deadline, lastFailure: FAILURES.has(code) ? code as IntakeFailure : UNKNOWN_FAILURE };
}

/** What a 404 means: the server no longer knows the session. */
function expiredStatus(): IntakeStatusUpdate {
  return { state: 'expired', deadline: Date.now() };
}

function parseFiles(value: unknown): IntakeFileInfo[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > INTAKE_MAX_FILES) return null;
  const files: IntakeFileInfo[] = [];
  let total = 0;
  for (const item of value) {
    if (!isRecord(item)) return null;
    const { size } = item;
    // A lone surrogate becomes U+FFFD, as File would store it anyway.
    const name = typeof item.name === 'string' ? item.name.replace(LONE_SURROGATES, '�') : null;
    if (name === null || !FILE_NAME_PATTERN.test(name)) return null;
    if (typeof size !== 'number' || !Number.isSafeInteger(size) || size < 0) return null;
    total += size;
    files.push({ name, size });
  }
  return total <= INTAKE_MAX_MESSAGE_BYTES ? files : null;
}

function writeEntry(session: IntakeSession, deleting: boolean, storage: Storage | null): boolean {
  if (!storage || !ID_PATTERN.test(session.id)) return false;
  try {
    const { id, secret, address, deadline } = session;
    const entry = deleting ? { id, secret, address, deadline, deleting } : { id, secret, address, deadline };
    storage.setItem(intakeStorageKey(id), JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
}

/** Every live resume entry; expired and malformed ones (and the old single-key entry) are removed. */
function readEntries(storage: Storage | null, now: number): StoredEntry[] {
  if (!storage) return [];
  let keys: string[];
  try {
    keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter((key): key is string => key !== null);
  } catch {
    return [];
  }
  const entries: StoredEntry[] = [];
  for (const key of keys) {
    if (key !== INTAKE_STORAGE_KEY && !key.startsWith(STORAGE_PREFIX)) continue;
    const entry = key === INTAKE_STORAGE_KEY ? null : readStoredEntry(storage, key, now);
    if (entry) entries.push(entry);
    else removeStorageKey(storage, key);
  }
  return entries;
}

function readStoredEntry(storage: Storage, key: string, now: number): StoredEntry | null {
  try {
    const raw = storage.getItem(key);
    const entry = raw === null ? null : parseStored(JSON.parse(raw), now);
    return entry && intakeStorageKey(entry.session.id) === key ? entry : null;
  } catch {
    return null;
  }
}

function removeStorageKey(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can throw (privacy mode, blocked site data); nothing to clean then.
  }
}

function isSessionRef(session: IntakeSessionRef): boolean {
  return ID_PATTERN.test(session.id) && SECRET_PATTERN.test(session.secret);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Epoch milliseconds (not seconds), as a safe integer. */
function isEpochMs(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= MIN_EPOCH_MS;
}

/** Time left as measured by the server: 0 up to one session lifetime. */
function isExpiresIn(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= INTAKE_SESSION_TTL_MS + EXPIRES_IN_SLACK_MS;
}

/** This device's deadline for an answer that arrived at `receivedAt` (whole ms, so it stores as a safe integer). */
function deadlineFrom(receivedAt: number, expiresInMs: number): number {
  return Math.floor(receivedAt + expiresInMs);
}

function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { readonly name?: unknown }).name === 'AbortError';
}

function sessionPath(id: string): string {
  return `${INTAKE_API_BASE}sessions/${id}`;
}

function authorization(session: IntakeSessionRef): Record<string, string> {
  return { Authorization: `Bearer ${session.secret}` };
}

function fileType(name: string): string {
  const extension = name.slice(name.lastIndexOf('.')).toLowerCase();
  if (extension === '.zip') return 'application/zip';
  if (extension === '.csv') return 'text/csv';
  return 'text/plain';
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function defaultTabStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function defaultLocks(): IntakeLockManager | null {
  try {
    const locks = typeof navigator === 'undefined' ? undefined : (navigator as Partial<Navigator>).locks;
    return locks && typeof locks.request === 'function' ? locks : null;
  } catch {
    return null;
  }
}
